<script setup lang="ts">
import { computed, useId } from 'vue';

/**
 * Couche SVG PARTAGÉE des liaisons d'un arbre de recherche (éditeur admin +
 * arbre joueur). Reçoit des liaisons déjà géométrisées par
 * `useResearchTreeEdges` (chemin orienté prérequis → dépendant) et les rend
 * avec une FLÈCHE (marker-end) sur le dépendant pour rendre le sens lisible.
 *
 * `variant` pilote la couleur (et la couleur de la flèche) :
 *  - met       : prérequis rempli / liaison normale → cyan plein
 *  - unmet     : prérequis manquant → gris pointillé
 *  - selected  : prérequis manquant de la recherche sélectionnée → rouge pointillé
 *  - warn      : prérequis sur un palier supérieur au dépendant → ambre pointillé
 *
 * `overlay` place le SVG AU-DESSUS des nœuds (éditeur) ; `clickable` rend le
 * trait cliquable (suppression d'une liaison) — sinon click-through total.
 */

type EdgeVariant = 'met' | 'unmet' | 'selected' | 'warn';

defineProps<{
  edges: { key: string; d: string; variant: EdgeVariant; title?: string }[];
  width: number;
  height: number;
  /** SVG au-dessus des nœuds (z-index) — sinon en dessous. */
  overlay?: boolean;
  /** Trait cliquable (émet `edgeClick`) — sinon non interactif. */
  clickable?: boolean;
}>();

const emit = defineEmits<{ edgeClick: [key: string] }>();

// Ids de markers uniques par instance (évite les collisions si plusieurs arbres
// sont montés sur la même page).
const uid = useId();
const VARIANTS: EdgeVariant[] = ['met', 'unmet', 'selected', 'warn'];
const markerId = (v: EdgeVariant): string => `rte-arrow-${v}-${uid}`;

const markers = computed(() => VARIANTS.map((v) => ({ variant: v, id: markerId(v) })));
</script>

<template>
  <svg
    class="rte-edges"
    :class="{ 'is-overlay': overlay, 'is-clickable': clickable }"
    :width="width"
    :height="height"
    :viewBox="`0 0 ${width} ${height}`"
  >
    <defs>
      <marker
        v-for="m in markers"
        :id="m.id"
        :key="m.id"
        class="rte-arrow"
        :class="`v-${m.variant}`"
        viewBox="0 0 10 10"
        refX="8.5"
        refY="5"
        markerWidth="7"
        markerHeight="7"
        orient="auto-start-reverse"
      >
        <path d="M 0 0 L 10 5 L 0 10 z" />
      </marker>
    </defs>
    <path
      v-for="e in edges"
      :key="e.key"
      :d="e.d"
      class="rte-edge"
      :class="`is-${e.variant}`"
      :marker-end="`url(#${markerId(e.variant)})`"
      fill="none"
      @click="clickable && emit('edgeClick', e.key)"
    >
      <title v-if="e.title">{{ e.title }}</title>
    </path>
  </svg>
</template>

<style lang="scss" scoped>
.rte-edges {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  pointer-events: none; // click-through par défaut (laisse passer vers les nœuds)
  &.is-overlay { z-index: 2; }
}
.rte-edge {
  stroke-width: 2;
  // met / normal : trait plein cyan (ouvre la voie).
  &.is-met { stroke: rgba($color-neon-cyan, 0.65); }
  // unmet : prérequis manquant → pointillé gris.
  &.is-unmet { stroke: rgba(160, 174, 192, 0.45); stroke-dasharray: 5 4; }
  // selected : prérequis manquant de la recherche sélectionnée → rouge.
  &.is-selected { stroke: rgba($color-danger, 0.85); stroke-dasharray: 5 4; stroke-width: 2.5; }
  // warn : prérequis sur un palier supérieur au dépendant → ambre.
  &.is-warn { stroke: rgba($color-warning, 0.75); stroke-dasharray: 6 4; }
}
// Mode cliquable (éditeur) : seul le trait capte le clic ; survol en rouge.
.is-clickable .rte-edge {
  cursor: pointer;
  pointer-events: stroke;
  &:hover { stroke: rgba($color-danger, 0.95); stroke-width: 3; }
}
// Flèches (sens prérequis → dépendant) : fill aligné sur la couleur de la liaison.
.rte-arrow.v-met path { fill: rgba($color-neon-cyan, 0.85); }
.rte-arrow.v-unmet path { fill: rgba(160, 174, 192, 0.6); }
.rte-arrow.v-selected path { fill: rgba($color-danger, 0.9); }
.rte-arrow.v-warn path { fill: rgba($color-warning, 0.9); }
</style>
